import React, { useEffect, useMemo, useState } from "react";
import { useTitle } from "../../context/TitleContext";
import PermissionButton from "../../components/PermissionButton";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProducts,
  deleteProduct,
  fetchAllCategories,
  fetchAllGrades,
} from "../../redux/Slices/ProductSlice";
import {
  fetchTeachersCodesByProduct,
} from "../../redux/Slices/TeachersCodesSlice";
import { getSettingsTeachers } from "../../redux/Slices/TeachersSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CiBarcode } from "react-icons/ci";
import { FaFileExcel, FaPlus, FaTrash, FaEdit, FaSearch, FaKey, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import Pagination from '../../components/Pagination';

export default function DigitalCodes() {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { settingsTeachers: teachers } = useSelector((state) => state.teachers);
  const {
    products,
    isLoading,
    meta,
  } = useSelector((state) => state.products);

  const {
    productCodes,
    productCodesMeta,
    productCodesLoading,
  } = useSelector((state) => state.teachers_codes);

  // Pagination & Filters
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState("");

  // Import Modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [importLoading, setImportLoading] = useState(false);

  // Codes View Modal (لعرض الأكواد المرتبطة بمنتج)
  const [showCodesModal, setShowCodesModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [codesPage, setCodesPage] = useState(1);

  useEffect(() => {
    setTitle("إدارة الأكواد الرقمية");
    dispatch(fetchAllCategories());
    dispatch(fetchAllGrades());
    dispatch(getSettingsTeachers());
  }, [setTitle, dispatch]);

  // جلب المنتجات من الـ API مع فلتر codes مباشرة
  useEffect(() => {
    const filters = {};
    if (searchQuery) filters.search = searchQuery;
    if (selectedTeacher) filters.teacher_id = selectedTeacher;
    // بنبعت category_type=codes عشان الباك يفلتر المنتجات ذات الكاتيجوري كودز
    filters.category_type = "codes";

    dispatch(fetchProducts({ page: currentPage, filters }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, selectedTeacher]);

  // عند البحث نرجع لأول صفحة
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    const filters = { category_type: "codes" };
    if (searchQuery) filters.search = searchQuery;
    if (selectedTeacher) filters.teacher_id = selectedTeacher;
    dispatch(fetchProducts({ page: 1, filters }));
  };

  const handleTeacherFilter = (val) => {
    setSelectedTeacher(val);
    setCurrentPage(1);
    const filters = { category_type: "codes" };
    if (searchQuery) filters.search = searchQuery;
    if (val) filters.teacher_id = val;
    dispatch(fetchProducts({ page: 1, filters }));
  };

  const handlePageChange = (page) => {
    if (isLoading) return;
    setCurrentPage(page);
  };

  // فلتر frontend احتياطي لو الباك مش بيدعم category_type
  const isCodeCategory = (catNameEn, catNameAr) => {
    if (!catNameEn && !catNameAr) return false;
    const nameEn = (catNameEn || "").toLowerCase().trim();
    const nameAr = (catNameAr || "").trim();
    return nameEn.includes("codes") || nameAr.includes("كود") || nameAr.includes("أكواد") || nameEn.includes("digital");
  };

  const filteredProducts = useMemo(() => {
    if (!products) return [];
    return products.filter((product) =>
      isCodeCategory(product.category_name_en, product.category_name_ar)
    );
  }, [products]);

  // فتح modal الأكواد المرتبطة بمنتج
  const handleViewCodes = (product) => {
    setSelectedProduct(product);
    setCodesPage(1);
    setShowCodesModal(true);
    dispatch(fetchTeachersCodesByProduct({
      teacher_id: product.teacher_id || "",
      grade_id: product.grade_id || "",
      page: 1,
    }));
  };

  const handleCodesPageChange = (page) => {
    setCodesPage(page);
    dispatch(fetchTeachersCodesByProduct({
      teacher_id: selectedProduct?.teacher_id || "",
      grade_id: selectedProduct?.grade_id || "",
      page,
    }));
  };

  const handleExcelChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setExcelFile(e.target.files[0]);
    }
  };

  const handleUploadExcel = async (e) => {
    e.preventDefault();
    if (!excelFile) { toast.error("يرجى اختيار ملف Excel"); return; }
    if (!selectedProductId) { toast.error("يرجى اختيار المنتج الرقمي لربطه بالأكواد"); return; }

    const formData = new FormData();
    formData.append("excel_file", excelFile);
    formData.append("product_id", selectedProductId);

    try {
      setImportLoading(true);
      const localData = localStorage.getItem("loggedUser");
      const parsed = localData ? JSON.parse(localData) : null;
      const token = parsed?.token || parsed?.data?.token || parsed?.result?.token || null;
      const baseUrl = process.env.REACT_APP_BASE_URL || "";
      const response = await axios.post(
        `${baseUrl}/dashboard/digital-codes/import`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      toast.success(response.data?.message || "تم استيراد الأكواد بنجاح");
      setShowImportModal(false);
      setExcelFile(null);
      setSelectedProductId("");
      dispatch(fetchProducts({ page: currentPage, filters: { category_type: "codes" } }));
    } catch (error) {
      toast.error(error.response?.data?.message || "حدث خطأ أثناء رفع واستيراد الأكواد");
    } finally {
      setImportLoading(false);
    }
  };

  const handleDelete = (product) => {
    Swal.fire({
      title: "هل أنت متأكد؟",
      text: `سيتم حذف المنتج الرقمي: ${product.title_ar || product.title_en}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "نعم، احذف",
      cancelButtonText: "إلغاء",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteProduct(product.id)).then((res) => {
          if (!res.error) {
            toast.success("تم الحذف بنجاح");
            dispatch(fetchProducts({ page: currentPage, filters: { category_type: "codes" } }));
          }
        });
      }
    });
  };


  const CodesPagination = () => {
    if (!productCodesMeta || productCodesMeta.last_page <= 1) return null;
    const pages = [];
    const start = Math.max(1, codesPage - 2);
    const end = Math.min(productCodesMeta.last_page, codesPage + 2);
    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`btn btn-sm mx-1 ${i === codesPage ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => handleCodesPageChange(i)}
          disabled={productCodesLoading}
        >
          {i}
        </button>
      );
    }
    return (
      <div className="d-flex justify-content-center align-items-center mt-2 gap-2">
        <button
          className="btn btn-sm btn-outline-secondary"
          disabled={codesPage === 1 || productCodesLoading}
          onClick={() => handleCodesPageChange(codesPage - 1)}
        >
          <FaChevronRight />
        </button>
        {pages}
        <button
          className="btn btn-sm btn-outline-secondary"
          disabled={codesPage === productCodesMeta.last_page || productCodesLoading}
          onClick={() => handleCodesPageChange(codesPage + 1)}
        >
          <FaChevronLeft />
        </button>
        <small className="text-muted">
          {productCodesMeta.current_page}/{productCodesMeta.last_page} ({productCodesMeta.total} كود)
        </small>
      </div>
    );
  };

  return (
    <div className="container-fluid py-4">
      <div className="card shadow-sm border-0 rounded-3 mb-4">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="m-0 font-weight-bold text-danger d-flex align-items-center gap-2">
            <CiBarcode style={{ fontSize: "28px" }} />
            إدارة الأكواد الرقمية (Digital Codes)
          </h5>
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn btn-success d-flex align-items-center gap-2 px-3 py-2 rounded-2"
              onClick={() => navigate("/products/add")}
            >
              <FaPlus /> إضافة منتج رقمي
            </button>
            <button
              className="btn btn-danger d-flex align-items-center gap-2 px-3 py-2 rounded-2"
              onClick={() => setShowImportModal(true)}
            >
              <FaFileExcel /> استيراد أكواد بالجملة
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* Filters */}
          <div className="row g-3 mb-4 align-items-end">
            <div className="col-md-5">
              <label className="form-label text-muted small">بحث بالاسم</label>
              <form className="input-group" onSubmit={handleSearch}>
                <span className="input-group-text bg-light border-end-0">
                  <FaSearch className="text-secondary" />
                </span>
                <input
                  type="text"
                  className="form-control bg-light border-start-0 ps-0"
                  placeholder="ابحث عن اسم المنتج الرقمي..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="btn btn-danger">بحث</button>
              </form>
            </div>

            <div className="col-md-4">
              <label className="form-label text-muted small">تصفية حسب المعلم</label>
              <select
                className="form-select bg-light"
                value={selectedTeacher}
                onChange={(e) => handleTeacherFilter(e.target.value)}
              >
                <option value="">كل المعلمين</option>
                {teachers?.map((tchr) => (
                  <option key={tchr.id} value={tchr.id}>
                    {tchr.name_ar || tchr.name_en}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedTeacher("");
                  setCurrentPage(1);
                  dispatch(fetchProducts({ page: 1, filters: { category_type: "codes" } }));
                }}
              >
                مسح الفلاتر
              </button>
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-danger" role="status" />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle text-center border">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>المنتج الرقمي</th>
                    <th>القسم</th>
                    <th>المعلم</th>
                    <th>المرحلة</th>
                    <th>السعر</th>
                    <th>المخزون</th>
                    <th>الحالة</th>
                    <th>الأكواد المرتبطة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product, idx) => (
                      <tr key={product.id}>
                        <td>{meta ? (meta.current_page - 1) * meta.per_page + idx + 1 : idx + 1}</td>
                        <td>
                          <div className="text-start d-flex align-items-center gap-2">
                            <span className="p-2 bg-light rounded text-danger">
                              <CiBarcode style={{ fontSize: "20px" }} />
                            </span>
                            <div>
                              <strong className="d-block text-dark">
                                {product.title_ar || product.title_en}
                              </strong>
                              <small className="text-muted text-uppercase font-monospace">
                                SKU: {product.sku || `DIG-${product.id}`}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>{product.category_name_ar || product.category_name_en || "—"}</td>
                        <td>{product.teacher_name || "—"}</td>
                        <td>{product.grade_name_ar || product.grade_name || "—"}</td>
                        <td>
                          <span className="fw-bold text-dark">
                            {product.real_price || product.price || "—"} ج.م
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              product.stock > 10
                                ? "bg-success-subtle text-success"
                                : product.stock > 0
                                ? "bg-warning-subtle text-warning"
                                : "bg-danger-subtle text-danger"
                            }`}
                            style={{ fontSize: "0.85rem" }}
                          >
                            {product.stock ?? 0} متوفر
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${product.status === "active" || product.status === 1 ? "bg-success" : "bg-secondary"}`}>
                            {product.status === "active" || product.status === 1 ? "نشط" : "غير نشط"}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary px-3 rounded-pill d-flex align-items-center gap-1 mx-auto"
                            onClick={() => handleViewCodes(product)}
                            title="عرض الأكواد المرتبطة بهذا المنتج"
                          >
                            <FaKey />
                            <span>الأكواد</span>
                          </button>
                        </td>
                        <td>
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              className="btn btn-sm btn-outline-warning px-3 rounded-pill"
                              onClick={() => navigate(`/products/edit/${product.id}`)}
                            >
                              <FaEdit className="me-1" /> تعديل
                            </button>
                            <PermissionButton
                              permission="delete-products"
                              className="btn btn-sm btn-outline-danger px-3 rounded-pill"
                              onClick={() => handleDelete(product)}
                            >
                              <FaTrash className="me-1" /> حذف
                            </PermissionButton>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-muted py-4">
                        لا توجد منتجات أكواد رقمية مسجلة.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <Pagination meta={meta} currentPage={currentPage} loading={isLoading} onChange={handlePageChange} />
            </div>
          )}
        </div>
      </div>

      {/* ===== Modal: الأكواد المرتبطة بالمنتج ===== */}
      {showCodesModal && selectedProduct && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header bg-primary text-white border-0 py-3">
                <div>
                  <h6 className="modal-title fw-bold d-flex align-items-center gap-2 mb-0">
                    <FaKey />
                    الأكواد المرتبطة بـ: {selectedProduct.title_ar || selectedProduct.title_en}
                  </h6>
                  <small className="opacity-75">
                    المعلم: {selectedProduct.teacher_name || "—"} | المرحلة: {selectedProduct.grade_name_ar || selectedProduct.grade_name || "—"}
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white ms-auto"
                  onClick={() => { setShowCodesModal(false); setSelectedProduct(null); }}
                />
              </div>
              <div className="modal-body py-3">
                {productCodesLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status" />
                    <p className="mt-2 text-muted small">جاري تحميل الأكواد...</p>
                  </div>
                ) : productCodes.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <FaKey size={40} className="mb-3 opacity-25" />
                    <p>لا توجد أكواد مرتبطة بهذا المنتج بعد.</p>
                    <small>يمكنك استيراد أكواد جديدة بالضغط على "استيراد أكواد بالجملة"</small>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover border text-center align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>الكود</th>
                            <th>الرقم التسلسلي</th>
                            <th>السعر</th>
                            <th>الحالة</th>
                            <th>تاريخ الإضافة</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productCodes.map((code, idx) => (
                            <tr key={code.id}>
                              <td>
                                {productCodesMeta
                                  ? (productCodesMeta.current_page - 1) * productCodesMeta.per_page + idx + 1
                                  : idx + 1}
                              </td>
                              <td>
                                <strong className="text-primary bg-primary-subtle px-2 py-1 rounded">
                                  {code.code}
                                </strong>
                              </td>
                              <td>
                                <small className="text-muted font-monospace">{code.serial || "—"}</small>
                              </td>
                              <td>
                                <span className="fw-bold">{code.price} ج.م</span>
                              </td>
                              <td>
                                <span className={`badge ${code.status === "active" ? "bg-success" : code.status === "used" ? "bg-warning text-dark" : "bg-secondary"}`}>
                                  {code.status === "active" ? "متاح" : code.status === "used" ? "مستخدم" : code.status || "—"}
                                </span>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {code.created_at ? new Date(code.created_at).toLocaleDateString("ar-EG") : "—"}
                                </small>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <CodesPagination />
                  </>
                )}
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-secondary rounded-pill px-4"
                  onClick={() => { setShowCodesModal(false); setSelectedProduct(null); }}
                >
                  إغلاق
                </button>
                <button
                  type="button"
                  className="btn btn-success rounded-pill px-4"
                  onClick={() => navigate("/teachers_codes")}
                >
                  إدارة كل الأكواد
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Modal: استيراد أكواد Excel ===== */}
      {showImportModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header bg-danger text-white border-0 py-3">
                <h6 className="modal-title fw-bold d-flex align-items-center gap-2">
                  <FaFileExcel />
                  رفع واستيراد الأكواد الرقمية (Bulk Excel Import)
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => { setShowImportModal(false); setExcelFile(null); }}
                />
              </div>
              <form onSubmit={handleUploadExcel}>
                <div className="modal-body py-4">
                  <div className="mb-3">
                    <label className="form-label fw-bold text-secondary small">
                      1. اختر المنتج الرقمي:
                    </label>
                    <select
                      className="form-select"
                      required
                      value={selectedProductId}
                      onChange={(e) => setSelectedProductId(e.target.value)}
                    >
                      <option value="">حدد منتج الأكواد المراد تغذيته...</option>
                      {filteredProducts?.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.title_ar || p.title_en}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold text-secondary small">
                      2. اختر ملف Excel (.xlsx / .xls):
                    </label>
                    <div className="border border-2 border-dashed rounded-3 p-4 text-center bg-light">
                      <FaFileExcel className="text-success mb-2" style={{ fontSize: "40px" }} />
                      <p className="small text-muted mb-3">
                        اسحب ملف البيانات هنا أو انقر لاختياره من جهازك
                      </p>
                      <input
                        type="file"
                        className="form-control"
                        accept=".xlsx, .xls"
                        required
                        onChange={handleExcelChange}
                      />
                      {excelFile && (
                        <div className="mt-3 text-success small fw-bold">
                          الملف المختار: {excelFile.name} ({(excelFile.size / 1024).toFixed(1)} KB)
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-light p-3 rounded-3 mb-1 small text-muted">
                    <strong className="text-dark d-block mb-1">تعليمات هيكل الملف المقبول:</strong>
                    يجب أن يحتوي ملف Excel على عمود باسم <code>code</code> أو أن يكون العمود الأول يحتوي على الأكواد مباشرة.
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button
                    type="button"
                    className="btn btn-light rounded-pill px-4"
                    onClick={() => { setShowImportModal(false); setExcelFile(null); }}
                    disabled={importLoading}
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="btn btn-danger rounded-pill px-4 d-flex align-items-center gap-2"
                    disabled={importLoading}
                  >
                    {importLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        جاري الرفع والمعالجة...
                      </>
                    ) : (
                      "بدء الاستيراد الفوري"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
